import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_LABEL = {
  pending_payment: "Awaiting Payment",
  issued: "Code Sent",
  redeemed: "Redeemed",
};

const STATUS_COLOR = {
  pending_payment: "border-amber-300 text-amber-800 bg-amber-50",
  issued: "border-emerald-300 text-emerald-800 bg-emerald-50",
  redeemed: "border-brand-accent text-brand-accent bg-brand-surface",
};

/**
 * Admin panel section for gift card management.
 * - Lists all gift cards newest first
 * - "Mark Paid" flips status to issued AND triggers the code email to the recipient
 * - "Mark Redeemed" flips status to redeemed
 */
export default function AdminGiftCards() {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const refresh = () => {
    setLoading(true);
    api
      .get("/admin/gift-cards")
      .then((r) => setGifts(r.data || []))
      .catch(() => toast.error("Could not load gift cards."))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, []);

  const markPaid = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/gift-cards/${id}/mark-paid`);
      toast.success("Gift card issued — recipient emailed.");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark paid.");
    } finally {
      setBusyId(null);
    }
  };

  const markRedeemed = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/gift-cards/${id}/mark-redeemed`);
      toast.success("Marked redeemed.");
      refresh();
    } catch {
      toast.error("Could not mark redeemed.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <p className="text-brand-neutral py-8">Loading gift cards…</p>;

  if (gifts.length === 0) {
    return (
      <p data-testid="giftcards-empty" className="text-brand-neutral py-8">
        No gift card purchases yet.
      </p>
    );
  }

  return (
    <div data-testid="admin-gift-cards" className="border-t border-brand-border">
      <div className="hidden md:grid grid-cols-12 gap-3 py-3 border-b border-brand-border text-[10px] uppercase tracking-widest text-brand-neutral">
        <div className="col-span-2">Reference</div>
        <div className="col-span-3">From → To</div>
        <div className="col-span-1 text-right">Amount</div>
        <div className="col-span-2">Code</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2 text-right">Actions</div>
      </div>
      <ul className="divide-y divide-brand-border">
        {gifts.map((g) => (
          <li
            key={g.id}
            data-testid={`gift-row-${g.id}`}
            className="py-5 grid md:grid-cols-12 gap-3 items-center"
          >
            <div className="md:col-span-2">
              <p className="font-serif text-brand-dark">#{g.id.slice(0, 8).toUpperCase()}</p>
              <p className="text-xs text-brand-neutral mt-1">
                {new Date(g.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="md:col-span-3 text-sm">
              <p className="text-brand-dark">
                {g.sender_name} <span className="text-brand-neutral">→</span> {g.recipient_name}
              </p>
              <p className="text-xs text-brand-neutral">
                {g.sender_email} → {g.recipient_email}
              </p>
              {g.message && (
                <p className="text-xs text-brand-neutral italic mt-1 line-clamp-2">"{g.message}"</p>
              )}
            </div>
            <div className="md:col-span-1 md:text-right font-medium text-brand-dark">
              ${g.amount.toFixed(2)}
            </div>
            <div className="md:col-span-2 font-mono text-xs text-brand-dark break-all">
              {g.code}
            </div>
            <div className="md:col-span-2">
              <span
                className={`inline-block px-3 py-1 border text-[10px] uppercase tracking-widest ${
                  STATUS_COLOR[g.status] || "border-brand-border text-brand-neutral"
                }`}
              >
                {STATUS_LABEL[g.status] || g.status}
              </span>
              {g.code_sent_at && (
                <p className="text-[10px] text-brand-neutral mt-1">
                  Sent {new Date(g.code_sent_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="md:col-span-2 md:text-right flex md:justify-end gap-2 flex-wrap">
              {g.status === "pending_payment" && (
                <button
                  data-testid={`gift-mark-paid-${g.id}`}
                  disabled={busyId === g.id}
                  onClick={() => markPaid(g.id)}
                  className="btn-primary-sm"
                >
                  {busyId === g.id ? "…" : "Mark Paid"}
                </button>
              )}
              {g.status === "issued" && (
                <button
                  data-testid={`gift-mark-redeemed-${g.id}`}
                  disabled={busyId === g.id}
                  onClick={() => markRedeemed(g.id)}
                  className="btn-outline text-xs px-3 py-1.5"
                >
                  {busyId === g.id ? "…" : "Mark Redeemed"}
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
